import Utilities from '@util/Utilities';
import Hotel from '@app/model/hotel/Hotel';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';
import Competitor from '@app/model/company_hotel/Competitor';
import _orderBy from 'lodash/orderBy';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';

class GraphUtilities {

	public static isFilterComputable = (filter: MarketStateFilterInput) => {
		return filter.ratesType === undefined || filter.platform === undefined || filter.provider === undefined;
	};

	public static findMainHotelData = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data: any[] = [];
		let lastFoundPrice = 0;

		marketStates.forEach(ms => {
			const dailyMainHotelSnapshot = ms.current.findDailyMainHotelSnapshot();
			const d = GraphUtilities.findHotelData(companyHotel, ms, dailyMainHotelSnapshot, lastFoundPrice);
			lastFoundPrice = d.y;
			data.push(d);
		});
		return data;
	};

	public static findCompetitorData = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[], competitor: Competitor) => {
		const data: any[] = [];
		let lastFoundPrice = 0;

		marketStates.forEach(ms => {
			const dailyCompetitorSnapshot = ms.current.findDailyCompetitorSnapshot(competitor.id);
			const d = GraphUtilities.findHotelData(companyHotel, ms, dailyCompetitorSnapshot, lastFoundPrice);
			lastFoundPrice = d.y;
			data.push(d);
		});

		return data;
	};

	public static findCompetitorsMedian = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data: any[] = [];
		let lastFoundPrice = 0;

		marketStates.forEach(ms => {
			const d = GraphUtilities.findCompetitorsMedianData(ms, lastFoundPrice);
			lastFoundPrice = d.y;
			data.push(d);
		});
		return data;
	};

	private static findHotelData = (companyHotel: CompanyHotel, ms: MarketStateDailySnapshotWithComparisonOutput, dailyCompetitorSnapshot: MarketStateStatDateCompetitorSnapshot | undefined, lastFoundPrice: number) => {
		if (!dailyCompetitorSnapshot || dailyCompetitorSnapshot.isEmpty() || dailyCompetitorSnapshot.isNotAvailable()) {
			return {
				name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
				marker: {
					symbol: 'cross',
					lineWidth: 2,
					lineColor: 'black',
				},
				y: lastFoundPrice,
				val: 0,
				displayValue: 'Not Available',
				roomType: '',
				hotelId: dailyCompetitorSnapshot ? 'competitor_' + dailyCompetitorSnapshot.competitorId : 'competitor_undefined',
			};
		} else if (dailyCompetitorSnapshot.isSoldOut()) {
			return {
				name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
				marker: {
					symbol: 'cross',
					lineWidth: 2,
					lineColor: 'black',
				},
				y: lastFoundPrice,
				val: 0,
				displayValue: 'Sold Out',
				roomType: '',
				hotelId: 'competitor_' + dailyCompetitorSnapshot.competitorId,
			};
		} else {
			const amount = parseFloat('' + dailyCompetitorSnapshot.getLowestItemForRoom().lowestItem.item.price.amount);
			return {
				name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
				marker: {
					symbol: 'circle',
					lineWidth: 2,
				},
				y: amount,
				val: amount,
				displayValue: Utilities.formatDouble(amount, 0),
				roomType: dailyCompetitorSnapshot!.getLowestItemForRoom().roomMappingType,
				roomName: dailyCompetitorSnapshot!.getLowestItemForRoom().findRoomName(companyHotel),
				hotelId: 'competitor_' + dailyCompetitorSnapshot.competitorId,
			};
		}
	};

	private static findCompetitorsMedianData = (ms: MarketStateDailySnapshotWithComparisonOutput, lastFoundPrice: number) => {
		const amount = !!ms.current.competitorsPriceStatistics?.medianPrice ? parseFloat('' + ms.current.competitorsPriceStatistics.medianPrice.amount) : 0.0;
		return {
			name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
			marker: {
				symbol: 'circle',
				lineWidth: 2,
			},
			y: amount,
			val: amount,
			displayValue: Utilities.formatDouble(amount, 0),
		};
	};

	public static seriesForMainHotel = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[], hidePriceMarkers = false) => {
		const originalData = GraphUtilities.findMainHotelData(companyHotel, marketStates);
		const data = hidePriceMarkers ? originalData.map(point => point.val > 0
			? { ...point, marker: { ...point.marker, enabled: false } } : point) : originalData;
		return {
			id: 'main-hotel',
			type: 'line',
			name: companyHotel.mainHotel.competitors[0].hotel.name,
			data: data,
			zoneAxis: 'x',
			zones: GraphUtilities.calculateLineTypeZones(data),
			lineWidth: 5,
		};
	};

	public static seriesForCompetitor = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[], competitor: Competitor) => {
		const data = GraphUtilities.findCompetitorData(companyHotel, marketStates, competitor);
		return {
			type: 'line',
			name: competitor.name,
			data: data,
			zoneAxis: 'x',
			zones: GraphUtilities.calculateLineTypeZones(data),
			lineWidth: 2,
		};
	};

	public static medianCompetitorsSeries = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data = GraphUtilities.findCompetitorsMedian(marketStates);
		return {
			type: 'line',
			name: 'Competitors Median',
			data: data,
			zoneAxis: 'x',
			lineWidth: 2,
		};
	};

	public static calculateLineTypeZones = (data: any[]): any[] => {
		let zones: any[] = [];

		for (let i = data.length - 1; i > -1; i--) {
			const d = data[i];
			if (d.val === 0) {
				zones.push({ value: i, dashStyle: 'dot' });
			} else if (d.val > 0) {
				zones.push({ value: i, dashStyle: 'solid' });
			}
		}
		zones.reverse();
		return zones;
	};

	public static findSoldOutHotelsData = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data: any[] = [];

		marketStates.forEach(ms => {
			const [soldOut, total] = ms.current.countSoldOutOverTotalCompetitors();


			// Scale up sold out values by 20x to make them more visible on price scale
			const scaledSoldOut = soldOut * 20;

			data.push({
				name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
				y: scaledSoldOut,
				actualSoldOut: soldOut,
				total: total,
				displayValue: `${soldOut}/${total}`,
			});
		});
		return data;
	};

	public static soldOutHotelsSeries = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data = GraphUtilities.findSoldOutHotelsData(marketStates);
		return {
			type: 'column',
			name: 'Sold Out Hotels',
			data: data,
			visible: false,
			yAxis: 0,
			color: '#ef4444',
			opacity: 0.7,
		};
	};

	public static findAvailabilityData = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data: any[] = [];

		marketStates.forEach(ms => {
			let totalAvailability = ms.current.findDailyMainHotelSnapshot()?.pmsAvailability?.availability || 0;

			// Scale up availability values by 5x to make them more visible on price scale
			const scaledAvailability = totalAvailability * 5;

			data.push({
				name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
				y: scaledAvailability,
				actualAvailability: totalAvailability,
				val: scaledAvailability,
				displayValue: totalAvailability.toString(),
			});
		});
		return data;
	};

	public static availabilitySeries = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data = GraphUtilities.findAvailabilityData(marketStates);
		return {
			type: 'line',
			name: 'Total Availability',
			data: data,
			visible: false,
			yAxis: 0,
			color: '#ef4444',
			lineWidth: 3,
		};
	};

	public static hasAvailabilityData = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]): boolean => {
		return marketStates.some(ms =>
			ms.current.countTotalAvailability() > 0,
		);
	};

	public static createRangeAreaData = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data: any[] = [];

		marketStates.forEach((ms, index) => {
			const stats = ms.current.competitorsPriceStatistics;
			if (stats && stats.minPrice && stats.maxPrice) {
				const minAmount = parseFloat('' + stats.minPrice.amount);
				const maxAmount = parseFloat('' + stats.maxPrice.amount);

				data.push([
					index, // x-axis position
					minAmount, // low value
					maxAmount,  // high value
				]);
			} else {
				// If no data available, use null values
				data.push([index, null, null]);
			}
		});

		return data;
	};

	public static createMedianLineData = (marketStates: MarketStateDailySnapshotWithComparisonOutput[], companyHotel?: CompanyHotel) => {
		const data: any[] = [];

		marketStates.forEach((ms, index) => {
			const price = companyHotel ? ms.current.comparisonPrice(companyHotel) : ms.current.competitorsPriceStatistics?.medianPrice;
			if (price) {
				const medianAmount = Number(price.amount);

				data.push({
					x: index,
					y: medianAmount,
					name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
					displayValue: Utilities.formatDouble(medianAmount, 0),
				});
			} else {
				data.push({
					x: index,
					y: null,
					name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
					displayValue: 'N/A',
				});
			}
		});

		return data;
	};

	public static competitorsPriceRangeSeries = (marketStates: MarketStateDailySnapshotWithComparisonOutput[]) => {
		const data = GraphUtilities.createRangeAreaData(marketStates);
		return {
			type: 'arearange',
			name: 'Competitors Price Range',
			data: data,
			zIndex: 0,
			color: 'rgba(124, 181, 236, 0.3)',
			fillOpacity: 0.5,
			lineWidth: 0,
			marker: {
				enabled: false,
			},
		};
	};

	public static competitorsMedianLineSeries = (marketStates: MarketStateDailySnapshotWithComparisonOutput[], companyHotel?: CompanyHotel) => {
		const data = GraphUtilities.createMedianLineData(marketStates, companyHotel);
		return {
			type: 'line',
			name: 'Competitors ' + (companyHotel?.comparisonLabel() ?? 'Median'),
			data: data,
			zIndex: 1,
			color: '#7cb5ec',
			lineWidth: 2,
			marker: {
				enabled: true,
				symbol: 'circle',
				radius: 3,
			},
		};
	};

	public static hiddenSeriesForCompetitor = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[], competitor: Competitor) => {
		const visibleSeries = GraphUtilities.seriesForCompetitor(companyHotel, marketStates, competitor);
		return {
			...visibleSeries,
			visible: false, // Make the series hidden
			showInLegend: false, // Hide from legend
			showInTooltip: true,
			enableMouseTracking: true, // Keep tooltip interaction
		};
	};

	public static sortCompetitorsByAveragePrice = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[], descending: boolean = true, competitorTagIds: readonly number[] = []) => {
		const competitorsWithAvg = companyHotel.visibleCompetitors(competitorTagIds).map(competitor => {
			const data = GraphUtilities.findCompetitorData(companyHotel, marketStates, competitor);
			const validPrices = data.filter(d => d.val > 0).map(d => d.val);
			const avgPrice = validPrices.length > 0 ? validPrices.reduce((sum, val) => sum + val, 0) / validPrices.length : 0;
			return { competitor, avgPrice };
		});

		return _orderBy(competitorsWithAvg, ['avgPrice'], [descending ? 'desc' : 'asc']).map(item => item.competitor);
	};

	public static sortAllSeriesByAveragePrice = (companyHotel: CompanyHotel, marketStates: MarketStateDailySnapshotWithComparisonOutput[], descending: boolean = true, hideLines: boolean = false, showMainHotel: boolean = true, competitorTagIds: readonly number[] = [], hideMainHotelPriceMarkers = false) => {
		const allSeries: { series: any, avgPrice: number, isMainHotel: boolean }[] = [];

		// Add main hotel
		const mainHotelData = GraphUtilities.findMainHotelData(companyHotel, marketStates);
		const mainValidPrices = mainHotelData.filter(d => d.val > 0).map(d => d.val);
		const mainAvgPrice = mainValidPrices.length > 0 ? mainValidPrices.reduce((sum, val) => sum + val, 0) / mainValidPrices.length : 0;
		allSeries.push({
			series: GraphUtilities.seriesForMainHotel(companyHotel, marketStates, hideMainHotelPriceMarkers),
			avgPrice: mainAvgPrice,
			isMainHotel: true,
		});

		// Add competitors
		companyHotel.visibleCompetitors(competitorTagIds).forEach(competitor => {
			const data = GraphUtilities.findCompetitorData(companyHotel, marketStates, competitor);
			const validPrices = data.filter(d => d.val > 0).map(d => d.val);
			const avgPrice = validPrices.length > 0 ? validPrices.reduce((sum, val) => sum + val, 0) / validPrices.length : 0;
			allSeries.push({
				series: GraphUtilities.seriesForCompetitor(companyHotel, marketStates, competitor),
				avgPrice: avgPrice,
				isMainHotel: false,
			});
		});

		const sortedSeries = _orderBy(allSeries, ['avgPrice'], [descending ? 'desc' : 'asc']);

		// If hideLines is true, hide the lines but keep them in tooltips
		if (hideLines) {
			return sortedSeries.map(item => ({
				...item.series,
				visible: showMainHotel && item.isMainHotel, // Show main hotel if showMainHotel is true
				showInLegend: showMainHotel && item.isMainHotel, // Show in legend if visible
				showInTooltip: true,
				enableMouseTracking: true, // Keep tooltip interaction
			}));
		}

		return sortedSeries.map(item => item.series);
	};
}

export default GraphUtilities;
